import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import './ScanQR.css';

const ScanQR = () => {
  const [scannedData, setScannedData] = useState(null);
  const [qrDetails, setQrDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState('');
  const [scanMode, setScanMode] = useState('upload'); // 'upload' or 'camera'
  const [cameraActive, setCameraActive] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  const detectDataType = (data) => {
    if (!data) return 'unknown';

    // URL detection
    if (data.match(/^(https?:\/\/|www\.)/i)) {
      return 'url';
    }

    // Email detection
    if (data.match(/^mailto:/i) || data.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return 'email';
    }

    // Phone detection
    if (data.match(/^tel:/i) || (data.match(/^\+?[\d\s\-()]+$/) && data.replace(/\D/g, '').length >= 10)) {
      return 'phone';
    }

    // Wi-Fi detection
    if (data.match(/^WIFI:/i)) {
      return 'wifi';
    }

    // vCard detection
    if (data.match(/^BEGIN:VCARD/i)) {
      return 'vcard';
    }

    return 'text';
  };

  const parseWiFi = (data) => {
    const ssidMatch = data.match(/S:([^;]+);/);
    const passwordMatch = data.match(/P:([^;]+);/);
    const securityMatch = data.match(/T:([^;]+);/);

    return {
      ssid: ssidMatch ? ssidMatch[1] : '',
      password: passwordMatch ? passwordMatch[1] : '',
      security: securityMatch ? securityMatch[1] : 'Unknown'
    };
  };

  const parseVCard = (data) => {
    const nameMatch = data.match(/FN:([^\n]+)/);
    const phoneMatch = data.match(/TEL:([^\n]+)/);
    const emailMatch = data.match(/EMAIL:([^\n]+)/);
    const orgMatch = data.match(/ORG:([^\n]+)/);

    return {
      name: nameMatch ? nameMatch[1] : '',
      phone: phoneMatch ? phoneMatch[1] : '',
      email: emailMatch ? emailMatch[1] : '',
      organization: orgMatch ? orgMatch[1] : ''
    };
  };

  // Camera scanning functionality
  const startCamera = async () => {
    try {
      setError('');
      setScannedData(null);
      setQrDetails(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setCameraActive(true);

        // Start scanning loop
        scanIntervalRef.current = setInterval(() => {
          scanQRFromCamera();
        }, 300);
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera. Please make sure you have granted camera permissions.');
      setScanMode('upload');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    setCameraActive(false);
  };

  const scanQRFromCamera = () => {
    if (!videoRef.current || !canvasRef.current || scannedData) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);

      if (code) {
        const dataType = detectDataType(code.data);
        setScannedData({
          raw: code.data,
          type: dataType
        });

        setQrDetails({
          version: code.version,
          errorCorrectionLevel: 'Unknown',
          maskPattern: 'Unknown',
          encoding: 'Unknown',
          matrixSize: `${code.version * 4 + 17} x ${code.version * 4 + 17}`
        });

        // Stop camera after successful scan
        stopCamera();
      }
    }
  };

  // Cleanup on unmount or mode change
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (scanMode === 'camera' && !cameraActive) {
      startCamera();
    } else if (scanMode === 'upload' && cameraActive) {
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setScannedData(null);
    setQrDetails(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          const dataType = detectDataType(code.data);
          setScannedData({
            raw: code.data,
            type: dataType
          });

          // Extract QR details
          setQrDetails({
            version: code.version,
            errorCorrectionLevel: 'Unknown', // jsQR doesn't provide this
            maskPattern: 'Unknown',
            encoding: 'Unknown',
            matrixSize: `${code.version * 4 + 17} x ${code.version * 4 + 17}`
          });
        } else {
          setError('No QR code detected in the image. Please try another image.');
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const renderDataDisplay = () => {
    if (!scannedData) return null;

    const { raw, type } = scannedData;

    switch (type) {
      case 'url':
        return (
          <div className="data-display">
            <div className="data-type">🌐 Website URL</div>
            <div className="data-content">
              <a href={raw} target="_blank" rel="noopener noreferrer">{raw}</a>
            </div>
            <div className="data-actions">
              <button onClick={() => window.open(raw, '_blank')}>
                🔗 Open Website
              </button>
              <button onClick={() => copyToClipboard(raw)}>
                📋 Copy URL
              </button>
            </div>
          </div>
        );

      case 'email':
        const email = raw.replace('mailto:', '');
        return (
          <div className="data-display">
            <div className="data-type">📧 Email Address</div>
            <div className="data-content">{email}</div>
            <div className="data-actions">
              <button onClick={() => window.location.href = `mailto:${email}`}>
                ✉️ Send Email
              </button>
              <button onClick={() => copyToClipboard(email)}>
                📋 Copy Email
              </button>
            </div>
          </div>
        );

      case 'phone':
        const phone = raw.replace('tel:', '');
        return (
          <div className="data-display">
            <div className="data-type">📞 Phone Number</div>
            <div className="data-content">{phone}</div>
            <div className="data-actions">
              <button onClick={() => window.location.href = `tel:${phone}`}>
                📱 Call
              </button>
              <button onClick={() => copyToClipboard(phone)}>
                📋 Copy Number
              </button>
            </div>
          </div>
        );

      case 'wifi':
        const wifiData = parseWiFi(raw);
        return (
          <div className="data-display">
            <div className="data-type">📶 Wi-Fi Network</div>
            <div className="data-content">
              <div className="wifi-info">
                <div><strong>Network:</strong> {wifiData.ssid}</div>
                <div><strong>Password:</strong> {wifiData.password}</div>
                <div><strong>Security:</strong> {wifiData.security}</div>
              </div>
            </div>
            <div className="data-actions">
              <button onClick={() => copyToClipboard(`SSID: ${wifiData.ssid}\nPassword: ${wifiData.password}`)}>
                📋 Copy Wi-Fi Info
              </button>
            </div>
          </div>
        );

      case 'vcard':
        const vcardData = parseVCard(raw);
        return (
          <div className="data-display">
            <div className="data-type">👤 Contact Information</div>
            <div className="data-content">
              <div className="vcard-info">
                {vcardData.name && <div><strong>Name:</strong> {vcardData.name}</div>}
                {vcardData.phone && <div><strong>Phone:</strong> {vcardData.phone}</div>}
                {vcardData.email && <div><strong>Email:</strong> {vcardData.email}</div>}
                {vcardData.organization && <div><strong>Organization:</strong> {vcardData.organization}</div>}
              </div>
            </div>
            <div className="data-actions">
              <button onClick={() => downloadVCard(raw)}>
                💾 Add to Contacts
              </button>
              <button onClick={() => copyToClipboard(raw)}>
                📋 Copy vCard
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="data-display">
            <div className="data-type">📝 Plain Text</div>
            <div className="data-content">
              <pre>{raw}</pre>
            </div>
            <div className="data-actions">
              <button onClick={() => copyToClipboard(raw)}>
                📋 Copy Text
              </button>
            </div>
          </div>
        );
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(err => {
      console.error('Copy failed:', err);
    });
  };

  const downloadVCard = (vcardData) => {
    const blob = new Blob([vcardData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'contact.vcf';
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="scan-qr">
      <div className="scan-container">
        <h2>Scan QR Code</h2>

        <div className="scan-mode-toggle">
          <button
            className={scanMode === 'upload' ? 'active' : ''}
            onClick={() => setScanMode('upload')}
          >
            📁 Upload Image
          </button>
          <button
            className={scanMode === 'camera' ? 'active' : ''}
            onClick={() => setScanMode('camera')}
          >
            📸 Use Camera
          </button>
        </div>

        {scanMode === 'upload' ? (
          <div className="upload-section">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            <button
              className="upload-btn"
              onClick={() => fileInputRef.current.click()}
            >
              📷 Upload QR Code Image
            </button>
            <p className="upload-hint">Upload an image containing a QR code</p>
          </div>
        ) : (
          <div className="camera-section">
            <div className="camera-container">
              <video
                ref={videoRef}
                className="camera-video"
                playsInline
              />
              {!scannedData && cameraActive && (
                <div className="scan-overlay">
                  <div className="scan-frame"></div>
                  <p className="scan-instruction">Position QR code within the frame</p>
                </div>
              )}
            </div>
            {cameraActive && !scannedData && (
              <button
                className="stop-camera-btn"
                onClick={() => setScanMode('upload')}
              >
                ⏹ Stop Camera
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {scannedData && (
          <div className="scan-results">
            {renderDataDisplay()}

            {qrDetails && (
              <div className="qr-details-section">
                <button
                  className="details-toggle"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? '▼' : '▶'} QR Technical Details
                </button>

                {showDetails && (
                  <div className="qr-details">
                    <div className="detail-item">
                      <span className="detail-label">QR Version:</span>
                      <span className="detail-value">{qrDetails.version}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Error Correction:</span>
                      <span className="detail-value">{qrDetails.errorCorrectionLevel}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Mask Pattern:</span>
                      <span className="detail-value">{qrDetails.maskPattern}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Encoding Mode:</span>
                      <span className="detail-value">{qrDetails.encoding}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Matrix Size:</span>
                      <span className="detail-value">{qrDetails.matrixSize}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default ScanQR;
